import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Save, MapPin, User, ShieldCheck, Trash2, Snowflake, Search, Globe, Clock, Bell } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import DashboardLayout from '../DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { getCityOptions, getTimezoneDisplayName } from '../../data/canadaCities';
import { getUTCOffset } from '../../utils/timezoneUtils';

const TrainerAccountSettings = ({ trainer: trainerProp, onUpdate, standalone = true }) => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [trainer, setTrainer] = useState(trainerProp || null);
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    phone: '',
    gender: 'other',
    age: '',
    whatsappVisibility: 'hidden',
    pricePerHour: '',
    biography: '',
    typeOfTraining: 'both',
    proficiency: '',
    proficiency2: '',
    country: '',
    city: '',
    street: '',
    timezone: '',
    fullAddress: '',
  });
  const [availableSpecs, setAvailableSpecs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [countriesList, setCountriesList] = useState([]);

  // Notification preferences state
  const [notificationPrefs, setNotificationPrefs] = useState({
    groupClassNotifications: true
  });
  const [notificationLoading, setNotificationLoading] = useState(false);

  // Timezone/City selector state
  const [cityOptions, setCityOptions] = useState([]);
  const [citySearchQuery, setCitySearchQuery] = useState('');
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  // Fetch notification preferences
  useEffect(() => {
    const fetchNotificationPrefs = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await fetch(`${API_BASE}/api/users/me/notification-preferences`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setNotificationPrefs({
            groupClassNotifications: data.group_class_notifications ?? true
          });
        }
      } catch (error) {
        console.error('Error fetching notification preferences:', error);
      }
    };
    fetchNotificationPrefs();
  }, [API_BASE]);

  const handleToggleGroupClassNotifications = async (enabled) => {
    setNotificationLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/users/me/notification-preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ group_class_notifications: enabled })
      });

      if (response.ok) {
        setNotificationPrefs({ ...notificationPrefs, groupClassNotifications: enabled });
        toast({
          title: 'Success',
          description: enabled ? 'Group class notifications enabled' : 'Group class notifications disabled'
        });
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update notification preferences', variant: 'destructive' });
      setNotificationPrefs({ ...notificationPrefs, groupClassNotifications: !enabled });
    } finally {
      setNotificationLoading(false);
    }
  };

  // Fetch countries from admin-managed list
  const fetchCountries = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/countries-list`);
      if (response.ok) {
        const data = await response.json();
        // Only show active countries
        const activeCountries = (data.countries || []).filter(c => c.active !== false);
        setCountriesList(activeCountries);
        
        // If trainer has no country set but there's only one country available, auto-select it
        if (!formData.country && activeCountries.length === 1) {
          setFormData(prev => ({ ...prev, country: activeCountries[0].name }));
        }
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  // Fetch data on mount
  useEffect(() => {
    const loadData = async () => {
      await fetchCountries();
      await fetchProficiencyIcons();
      if (!trainerProp) {
        await fetchTrainerData();
      } else {
        setTrainer(trainerProp);
      }
    };
    loadData();
  }, []); // eslint-disable-line

  // Scroll to top when component mounts (for tab navigation)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Update form when trainer data changes
  useEffect(() => {
    if (trainer) {
      console.log('Updating formData with trainer:', trainer);
      const trainerCountry = trainer.location?.country || trainer.country || '';
      setFormData({
        firstname: trainer.firstname || '',
        lastname: trainer.lastname || '',
        phone: trainer.phone || '',
        gender: trainer.gender || 'other',
        age: trainer.age || '',
        whatsappVisibility: trainer.whatsappVisibility || 'hidden',
        pricePerHour: trainer.pricePerHour || trainer.price_per_hour || '',
        biography: trainer.biography || trainer.bio || '',
        typeOfTraining: trainer.typeOfTraining || 'both',
        proficiency: trainer.proficiency || '',
        proficiency2: trainer.proficiency2 || '',
        country: trainerCountry,
        city: trainer.location?.city || trainer.city || '',
        street: trainer.location?.street || trainer.street || '',
        timezone: trainer.timezone || '',
        fullAddress: trainer.fullAddress || '',
      });
    }
  }, [trainer]);

  // Auto-select first country if trainer has none and countries list is loaded
  useEffect(() => {
    if (countriesList.length > 0 && !formData.country) {
      setFormData(prev => ({ ...prev, country: countriesList[0].name }));
    }
  }, [countriesList, formData.country]);

  // Load city options for timezone selector
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const options = await getCityOptions();
        setCityOptions(options);
      } catch (error) {
        console.error('Error fetching cities:', error);
      }
    };
    fetchCities();
  }, []);

  // Set selected city when trainer data loads
  useEffect(() => {
    if (trainer && cityOptions.length > 0 && !selectedCity) {
      const trainerCity = trainer.location?.city || trainer.city;
      const trainerTimezone = trainer.timezone;
      if (trainerCity && trainerTimezone) {
        const currentCity = cityOptions.find(opt => 
          opt.city === trainerCity && opt.timezone === trainerTimezone
        );
        if (currentCity) {
          setSelectedCity(currentCity);
        }
      }
    }
  }, [trainer, cityOptions, selectedCity]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isCityDropdownOpen && !event.target.closest('.trainer-timezone-dropdown')) {
        setIsCityDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCityDropdownOpen]);

  // Filter cities based on search query
  const filteredCities = useMemo(() => {
    if (!citySearchQuery) return cityOptions.slice(0, 50);
    const query = citySearchQuery.toLowerCase();
    return cityOptions.filter(opt => 
      opt.city.toLowerCase().includes(query) ||
      opt.province.toLowerCase().includes(query)
    ).slice(0, 100);
  }, [cityOptions, citySearchQuery]);

  const handleCitySelect = (option) => {
    setSelectedCity(option);
    setCitySearchQuery('');
    setIsCityDropdownOpen(false);
    // Update form data with selected city's timezone
    setFormData(prev => ({
      ...prev,
      city: option.city,
      timezone: option.timezone,
    }));
  };

  const fetchTrainerData = async () => {
    try {
      console.log('Fetching trainer data from API...');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/trainers/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Trainer data received:', {
          firstname: data.firstname,
          lastname: data.lastname,
          proficiency: data.proficiency,
          typeOfTraining: data.typeOfTraining
        });
        setTrainer(data);
      } else {
        console.error('Failed to fetch trainer data:', response.status);
      }
    } catch (error) {
      console.error('Error fetching trainer data:', error);
    }
  };

  const fetchProficiencyIcons = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/proficiency-icons`);
      if (response.ok) {
        const data = await response.json();
        setAvailableSpecs(data);
      }
    } catch (error) {
      console.error('Error fetching proficiency icons:', error);
    }
  };

  const handleSetLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          // Use geocoding API to get address
          toast({
            title: 'Location Retrieved',
            description: `Lat: ${latitude}, Lng: ${longitude}`
          });
        },
        (error) => {
          toast({
            title: 'Location Error',
            description: 'Could not retrieve location',
            variant: 'destructive'
          });
        }
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      // Prepare specializations array from proficiency fields
      const specializations = [];
      if (formData.proficiency) {
        specializations.push(formData.proficiency);
      }
      if (formData.proficiency2 && formData.proficiency2 !== formData.proficiency) {
        specializations.push(formData.proficiency2);
      }
      
      // Prepare payload with location data from selectedCity
      const payload = {
        firstname: formData.firstname,
        lastname: formData.lastname,
        phone: formData.phone,
        gender: formData.gender,
        age: parseInt(formData.age),
        whatsappVisibility: formData.whatsappVisibility,
        pricePerHour: parseFloat(formData.pricePerHour),
        biography: formData.biography,
        typeOfTraining: formData.typeOfTraining,
        proficiency: formData.proficiency,
        proficiency2: formData.proficiency2,
        specializations: specializations.length > 0 ? specializations : undefined,
        country: formData.country,
        city: formData.city,
        street: formData.street,
        timezone: formData.timezone,
        fullAddress: formData.fullAddress,
      };

      // Add location data from selectedCity if available
      if (selectedCity) {
        payload.city = selectedCity.city;
        payload.province = selectedCity.province;
        payload.timezone = selectedCity.timezone;
        payload.country = selectedCity.country || 'Canada';
      }
      
      // Update trainer info with all new fields including location
      const trainerResponse = await fetch(`${API_BASE}/api/trainers/me`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (trainerResponse.ok) {
        toast({
          title: 'Success',
          description: 'Profile updated successfully'
        });
        
        // Update user info in AuthContext if firstname or lastname changed
        if ((formData.firstname || formData.lastname) && user) {
          const updatedName = `${formData.firstname || ''} ${formData.lastname || ''}`.trim();
          console.log('[TrainerAccountSettings] Updating user name:', updatedName);
          console.log('[TrainerAccountSettings] Current user:', user);
          if (updatedName) {
            setUser({
              ...user,
              name: updatedName
            });
            console.log('[TrainerAccountSettings] User name updated in context');
          }
        }
        
        if (onUpdate) {
          onUpdate();
        }
      } else {
        throw new Error('Failed to update profile');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update profile',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const content = (
    <div className="space-y-6 max-w-7xl mx-auto px-4 py-6">
      {/* Page Header */}
      {standalone && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-black">
              Account Settings
            </h1>
            <p className="text-gray-600 mt-2">Manage your profile information and account preferences</p>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/trainer/dashboard')}
            className="border-2 border-red-200 hover:bg-red-50"
          >
            Back to Dashboard
          </Button>
        </div>
      )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information Card */}
          <div className="border-2 border-red-200 dark:border-gray-700 shadow-lg rounded-lg bg-white dark:bg-gray-800">
            <div className="p-6 bg-gradient-to-r from-red-50 to-white dark:from-gray-800 dark:to-gray-800 border-b border-gray-200 dark:border-gray-700 rounded-t-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                  <User className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Personal Information
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Update your basic profile details</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="firstname">Firstname</Label>
                  <Input
                    id="firstname"
                    value={formData.firstname}
                    onChange={(e) => setFormData({ ...formData, firstname: e.target.value })}
                    placeholder="John"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="lastname">Lastname</Label>
                  <Input
                    id="lastname"
                    value={formData.lastname}
                    onChange={(e) => setFormData({ ...formData, lastname: e.target.value })}
                    placeholder="Doe"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="16478247355"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="gender">Gender</Label>
                  <Select value={formData.gender} onValueChange={(value) => setFormData({ ...formData, gender: value })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="age">Age</Label>
                  <Input
                    id="age"
                    type="number"
                    value={formData.age}
                    onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                    placeholder="18"
                    min="18"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="whatsapp">Whatsapp Visibility</Label>
                  <Select value={formData.whatsappVisibility} onValueChange={(value) => setFormData({ ...formData, whatsappVisibility: value })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select visibility" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="visible">Visible</SelectItem>
                      <SelectItem value="hidden">Hidden</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="price">Price per hour</Label>
                  <Input
                    id="price"
                    type="number"
                    value={formData.pricePerHour}
                    onChange={(e) => setFormData({ ...formData, pricePerHour: e.target.value })}
                    placeholder="40"
                    min="0"
                    step="0.01"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="biography">Biography</Label>
                  <Textarea
                    id="biography"
                    value={formData.biography}
                    onChange={(e) => setFormData({ ...formData, biography: e.target.value })}
                    placeholder="Tell about your experience..."
                    rows={4}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="training-type">Type Of Training</Label>
                  <Select value={formData.typeOfTraining} onValueChange={(value) => setFormData({ ...formData, typeOfTraining: value })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select training type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="both">Both</SelectItem>
                      <SelectItem value="online">Online</SelectItem>
                      <SelectItem value="in-person">In-Person</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="proficiency">Proficiency</Label>
                  <Select value={formData.proficiency} onValueChange={(value) => setFormData({ ...formData, proficiency: value })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select proficiency" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableSpecs.map((spec) => (
                        <SelectItem key={spec.id} value={spec.name}>
                          {spec.icon_url} {spec.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="proficiency2">Proficiency 2</Label>
                  <Select value={formData.proficiency2} onValueChange={(value) => setFormData({ ...formData, proficiency2: value })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select second proficiency" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableSpecs.map((spec) => (
                        <SelectItem key={spec.id} value={spec.name}>
                          {spec.icon_url} {spec.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="country">Country</Label>
                  <Select value={formData.country} onValueChange={(value) => setFormData({ ...formData, country: value })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countriesList.length > 0 ? (
                        countriesList.map((country) => (
                          <SelectItem key={country.id || country.name} value={country.name}>
                            {country.flag_emoji && `${country.flag_emoji} `}{country.name}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="px-2 py-1.5 text-sm text-gray-500">No countries available</div>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Toronto"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="street">Street</Label>
                  <Input
                    id="street"
                    value={formData.street}
                    onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                    placeholder="123 Main St"
                    className="mt-1"
                  />
                </div>

                <div className="trainer-timezone-dropdown">
                  <Label className="flex items-center gap-2 mb-1">
                    <Globe className="w-4 h-4 text-red-600" />
                    City & Timezone
                  </Label>
                  
                  {/* Current Timezone Display */}
                  {formData.timezone && (
                    <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded-md mb-2 text-sm">
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                        <Clock className="w-3 h-3 text-red-600" />
                        <span>Current: <strong>{formData.city || 'Not set'}</strong></span>
                        <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded text-xs font-medium">
                          {getUTCOffset(formData.timezone)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 ml-5">
                        {getTimezoneDisplayName(formData.timezone)}
                      </p>
                    </div>
                  )}

                  <div className="relative">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="text"
                        value={isCityDropdownOpen ? citySearchQuery : (selectedCity ? `${selectedCity.city}, ${selectedCity.province}` : '')}
                        onChange={(e) => {
                          setCitySearchQuery(e.target.value);
                          setIsCityDropdownOpen(true);
                        }}
                        onFocus={() => setIsCityDropdownOpen(true)}
                        placeholder="Search for your city..."
                        className="pl-10 border-2 border-gray-200 dark:border-gray-600 focus:border-red-500"
                      />
                    </div>
                    {isCityDropdownOpen && (
                      <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredCities.length === 0 ? (
                          <div className="p-4 text-center text-gray-500">
                            {citySearchQuery ? 'No cities found' : 'Loading cities...'}
                          </div>
                        ) : (
                          filteredCities.map((option, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => handleCitySelect(option)}
                              className={`w-full px-4 py-2 text-left hover:bg-red-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
                                selectedCity?.city === option.city && selectedCity?.province === option.province 
                                  ? 'bg-red-50 dark:bg-gray-700' 
                                  : ''
                              }`}
                            >
                              <div className="font-medium text-gray-900 dark:text-white text-sm">{option.city}, {option.province}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">{option.timezoneDisplay}</div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Selected City Preview */}
                  {selectedCity && (
                    <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded-md border border-green-200 dark:border-green-700">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-green-800 dark:text-green-300 text-sm">{selectedCity.city}, {selectedCity.province}</p>
                            <span className="px-2 py-0.5 bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300 rounded text-xs font-medium">
                              {getUTCOffset(selectedCity.timezone)}
                            </span>
                          </div>
                          <p className="text-xs text-green-600 dark:text-green-400">{selectedCity.timezoneDisplay}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="full-address">Full Address</Label>
                  <Textarea
                    id="full-address"
                    value={formData.fullAddress}
                    onChange={(e) => setFormData({ ...formData, fullAddress: e.target.value })}
                    placeholder="239 Foxhole Cir, Oakville, ON L6M 4X4, Canada"
                    rows={2}
                    className="mt-1"
                  />
                  <Button
                    type="button"
                    variant="link"
                    onClick={handleSetLocation}
                    className="text-red-600 hover:text-red-700 px-0 mt-1"
                  >
                    <MapPin className="w-4 h-4 mr-1" />
                    Click to set your location data!
                  </Button>
                </div>
              </div>
            </div>

              <div className="flex justify-end mt-6 pt-6 border-t">
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="bg-gradient-to-r from-red-600 to-black hover:from-red-700 hover:to-gray-900 px-8"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        </form>

        {/* Notification Settings Section */}
        <Card className="border-2 border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Notification Settings
            </CardTitle>
            <CardDescription>Manage your email notification preferences</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Group Class Notifications */}
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg mt-0.5">
                  <Bell className="w-4 h-4 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">Group Class Notifications</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Receive email reminders for group classes you&apos;ve joined
                  </p>
                  <ul className="text-xs text-gray-400 dark:text-gray-500 mt-2 space-y-1">
                    <li>• 24 hours before class starts</li>
                    <li>• 1 hour before class starts</li>
                    <li>• When a class is cancelled</li>
                  </ul>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {notificationLoading && (
                  <span className="text-xs text-gray-400">Saving...</span>
                )}
                <Switch
                  checked={notificationPrefs.groupClassNotifications}
                  onCheckedChange={handleToggleGroupClassNotifications}
                  disabled={notificationLoading}
                  data-testid="group-class-notifications-toggle"
                />
              </div>
            </div>

            {/* Info box */}
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-xs text-blue-600 dark:text-blue-400">
                <strong>Note:</strong> You will always receive important account-related emails regardless of these settings.
              </p>
            </div>
          </CardContent>
        </Card>
        
      {/* Account Management Section */}
      <AccountManagement />
    </div>
  );

  return standalone ? (
    <DashboardLayout>{content}</DashboardLayout>
  ) : (
    content
  );
};

// Account Management Component
const AccountManagement = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [showFreezeModal, setShowFreezeModal] = useState(false);
  const [showUnfreezeModal, setShowUnfreezeModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [accountStatus, setAccountStatus] = useState('active');
  const [loading, setLoading] = useState(false);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  const fetchAccountStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/trainer/account/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAccountStatus(data.status);
      }
    } catch (error) {
      console.error('Error fetching account status:', error);
    }
  };

  useEffect(() => {
    fetchAccountStatus();
  }, []);

  const handleFreeze = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/trainer/account/freeze`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast({
          title: 'Account Frozen',
          description: 'Your account has been frozen. You can unfreeze it anytime.',
        });
        setShowFreezeModal(false);
        fetchAccountStatus();
      } else {
        throw new Error('Failed to freeze account');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to freeze account',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUnfreeze = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/trainer/account/unfreeze`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast({
          title: 'Account Reactivated',
          description: 'Your account is now active again!',
        });
        setShowUnfreezeModal(false);
        fetchAccountStatus();
      } else {
        throw new Error('Failed to unfreeze account');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to reactivate account',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (confirmText !== 'DELETE MY ACCOUNT') {
      toast({
        title: 'Error',
        description: 'Please type exactly: DELETE MY ACCOUNT',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/trainer/account?confirmation_text=${encodeURIComponent(confirmText)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast({
          title: 'Account Deleted',
          description: 'Your account has been permanently deleted. Logging you out...',
        });
        
        // Clear all authentication data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // Call logout from AuthContext to clear state
        logout();
        
        // Redirect to home page after 2 seconds
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete account');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete account',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border-2 border-red-200 dark:border-gray-700 shadow-lg rounded-lg bg-white dark:bg-gray-800">
      <div className="p-6 bg-gradient-to-r from-red-50 to-white dark:from-gray-800 dark:to-gray-800 border-b border-gray-200 dark:border-gray-700 rounded-t-lg">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
            <ShieldCheck className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Account Management</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Manage your account security and privacy settings</p>
          </div>
        </div>
      </div>
      <div className="p-6 space-y-6">
        {/* Current Status Alert */}
        {accountStatus === 'frozen' && (
          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-500 rounded-lg">
            <div className="flex items-start gap-3">
              <Snowflake className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-yellow-900 dark:text-yellow-300">
                  Your account is currently frozen
                </p>
                <p className="text-xs text-yellow-800 dark:text-yellow-400 mt-1">
                  Your profile is hidden from search and you cannot receive new bookings. Click below to reactivate.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Freeze/Unfreeze Account */}
        <div className="p-6 border-2 border-gray-200 dark:border-gray-600 rounded-lg hover:border-gray-300 dark:hover:border-gray-500 transition-colors bg-white dark:bg-gray-800/50">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-lg ${accountStatus === 'frozen' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-yellow-100 dark:bg-yellow-900/30'}`}>
              <Snowflake className={`w-6 h-6 ${accountStatus === 'frozen' ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'}`} />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900 dark:text-white text-lg mb-2">
                {accountStatus === 'frozen' ? 'Reactivate Your Account' : 'Freeze Your Account'}
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {accountStatus === 'frozen' 
                  ? 'Unfreeze your account to become visible in search results and start accepting new bookings from clients.'
                  : 'Temporarily pause your profile visibility and stop receiving new booking requests. You can reactivate anytime.'
                }
              </p>
              <Button
                variant={accountStatus === 'frozen' ? 'default' : 'outline'}
                onClick={() => accountStatus === 'frozen' ? setShowUnfreezeModal(true) : setShowFreezeModal(true)}
                className={accountStatus === 'frozen' 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'border-2 border-yellow-400 dark:border-yellow-600 text-yellow-700 dark:text-yellow-400 hover:bg-yellow-50 dark:hover:bg-yellow-900/20'
                }
              >
                {accountStatus === 'frozen' ? (
                  <>
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    Unfreeze Account
                  </>
                ) : (
                  <>
                    <Snowflake className="w-4 h-4 mr-2" />
                    Freeze Account
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Delete Account */}
        <div className="p-6 border-2 border-red-200 dark:border-red-700 rounded-lg bg-red-50 dark:bg-red-900/20 hover:border-red-300 dark:hover:border-red-600 transition-colors">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
              <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-red-700 dark:text-red-300 text-lg mb-2">Delete Your Account</h4>
              <p className="text-sm text-red-600 dark:text-red-400 mb-4">
                Permanently remove your account and all associated data. This action is irreversible and cannot be undone.
              </p>
              <Button
                variant="destructive"
                onClick={() => setShowDeleteModal(true)}
                className="bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Account
              </Button>
            </div>
          </div>
        </div>

        {/* Freeze Modal */}
        {showFreezeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
              <h3 className="text-lg font-semibold mb-4">Freeze Your Account?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Your profile will be hidden from search and you won&apos;t receive new booking requests. You can unfreeze your account anytime.
              </p>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowFreezeModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleFreeze} disabled={loading} className="flex-1 bg-yellow-600 hover:bg-yellow-700">
                  {loading ? 'Freezing...' : 'Freeze Account'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Unfreeze Modal */}
        {showUnfreezeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
              <h3 className="text-lg font-semibold mb-4">Reactivate Your Account?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Your profile will become visible again and you&apos;ll start receiving booking requests.
              </p>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowUnfreezeModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleUnfreeze} disabled={loading} className="flex-1 bg-green-600 hover:bg-green-700">
                  {loading ? 'Activating...' : 'Reactivate Account'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-4">Delete Account Permanently?</h3>
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  This will permanently delete your account. This action cannot be undone.
                </p>
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-xs text-red-600 dark:text-red-400 font-medium">
                    ⚠️ Warning: All your data will be permanently deleted including:
                  </p>
                  <ul className="text-xs text-red-600 dark:text-red-400 mt-2 ml-4 list-disc">
                    <li>Your profile and reviews</li>
                    <li>All bookings and sessions</li>
                    <li>Messages and conversations</li>
                  </ul>
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2">Type &quot;DELETE MY ACCOUNT&quot; to confirm:</Label>
                  <Input
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="DELETE MY ACCOUNT"
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => { setShowDeleteModal(false); setConfirmText(''); }} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleDelete} 
                  disabled={loading || confirmText !== 'DELETE MY ACCOUNT'}
                  className="flex-1"
                >
                  {loading ? 'Deleting...' : 'Delete Forever'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainerAccountSettings;